import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  Download,
  FilterList,
  TrendingUp,
  People,
  Folder,
  AttachMoney,
  Assignment,
  Assessment,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { canViewFirmFinancials } from '../../utils/permissions';
import { getAuthToken } from '../../lib/authToken';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ReportsPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Something went wrong loading the reports. Please refresh the page and try again.
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

const ReportsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('this_month');
  const theme = useTheme();
  const { user } = useAuth();
  const showFinancialTabs = canViewFirmFinancials(user);
  const [sampleFallback, setSampleFallback] = useState<any | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch reports data with error handling
  const { data: casesData, isLoading: casesLoading, error: casesError } = useQuery({
    queryKey: ['reports', 'cases', user?.tenantId, period],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/cases?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch case reports');
        return response.json();
      } catch (error) {
        console.error('Error fetching case reports:', error);
        throw error;
      }
    },
    retry: 1,
  });

  const { data: financialData, isLoading: financialLoading, error: financialError } = useQuery({
    queryKey: ['reports', 'financial', user?.tenantId, period],
    enabled: showFinancialTabs,
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/financial?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch financial reports');
        return response.json();
      } catch (error) {
        console.error('Error fetching financial reports:', error);
        throw error;
      }
    },
    retry: 1,
  });

  const { data: clientsData, isLoading: clientsLoading, error: clientsError } = useQuery({
    queryKey: ['reports', 'clients', user?.tenantId, period],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/clients?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch client reports');
        return response.json();
      } catch (error) {
        console.error('Error fetching client reports:', error);
        throw error;
      }
    },
    retry: 1,
  });

  const { data: productivityData, isLoading: productivityLoading, error: productivityError } = useQuery({
    queryKey: ['reports', 'productivity', user?.tenantId, period],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/productivity?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch productivity reports');
        return response.json();
      } catch (error) {
        console.error('Error fetching productivity reports:', error);
        throw error;
      }
    },
    retry: 1,
  });

  const { data: billingData, isLoading: billingLoading, error: billingError } = useQuery({
    queryKey: ['reports', 'billing', user?.tenantId, period],
    enabled: showFinancialTabs,
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/billing?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch billing reports');
        return response.json();
      } catch (error) {
        console.error('Error fetching billing reports:', error);
        throw error;
      }
    },
    retry: 1,
  });

  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useQuery({
    queryKey: ['reports', 'analytics', user?.tenantId, period],
    enabled: showFinancialTabs,
    queryFn: async () => {
      try {
        const response = await fetch(`/api/reports/analytics?period=${period}`, {
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch analytics');
        return response.json();
      } catch (error) {
        console.error('Error fetching analytics:', error);
        throw error;
      }
    },
    retry: 1,
  });

  // Development-only: fetch sample stub data when real endpoints fail or return no data
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;

    const shouldFetchSample = (
      (!casesData?.data && casesError) ||
      (!financialData?.data && financialError) ||
      (!clientsData?.data && clientsError) ||
      (!productivityData?.data && productivityError) ||
      (!billingData?.data && billingError) ||
      (!analyticsData?.data && analyticsError)
    );

    if (shouldFetchSample) {
      fetch('/api/reports/sample-data')
        .then(res => res.json())
        .then((payload) => {
          if (payload && payload.success && payload.data) {
            setSampleFallback(payload.data);
          }
        })
        .catch(err => console.warn('Failed to load sample report data:', err));
    }
  }, [casesData, casesError, financialData, financialError, clientsData, clientsError, productivityData, productivityError, billingData, billingError, analyticsData, analyticsError]);

  // Build lightweight derived data from sampleFallback (for development only)
  const buildFallbackFor = (type: string) => {
    if (!sampleFallback) return null;

    try {
      if (type === 'cases' && Array.isArray(sampleFallback.cases)) {
        const arr = sampleFallback.cases;
        const statistics = {
          totalCases: arr.length,
          activeCases: arr.filter((c: any) => c.status === 'active').length,
          closedCases: arr.filter((c: any) => c.status === 'closed').length,
          pendingCases: arr.filter((c: any) => c.status === 'pending').length,
        };
        const statusDistribution = arr.reduce((acc: any, c: any) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
        const typeDistribution = arr.reduce((acc: any, c: any) => { acc[c.type || c.caseType || 'unknown'] = (acc[c.type || c.caseType || 'unknown'] || 0) + 1; return acc; }, {});
        const monthlyTrend = arr.reduce((acc: any, c: any) => { const month = c.createdAt ? new Date(c.createdAt).toISOString().slice(0,7) : 'unknown'; acc[month] = (acc[month] || 0) + 1; return acc; }, {});
        return { cases: arr, statistics, statusDistribution, typeDistribution, monthlyTrend };
      }

      if (type === 'financial' && Array.isArray(sampleFallback.invoices)) {
        const arr = sampleFallback.invoices;
        const statistics = {
          totalInvoiced: arr.reduce((s: number, i: any) => s + (i.amount || 0), 0),
          totalPaid: arr.filter((i: any) => i.status === 'paid').reduce((s: number, i: any) => s + (i.amount || 0), 0),
          totalOutstanding: arr.filter((i: any) => i.status !== 'paid').reduce((s: number, i: any) => s + (i.amount || 0), 0),
          paidInvoices: arr.filter((i: any) => i.status === 'paid').length,
        };
        const monthlyRevenue = arr.reduce((acc: any, inv: any) => { const m = inv.createdAt ? new Date(inv.createdAt).toISOString().slice(0,7) : 'unknown'; acc[m] = (acc[m] || 0) + (inv.amount || 0); return acc; }, {});
        const topClients = (arr.reduce((acc: any, inv: any) => { const name = inv.client ? `${inv.client.firstName} ${inv.client.lastName}` : 'Unknown'; acc[name] = (acc[name] || 0) + (inv.amount || 0); return acc; }, {}));
        const topClientsArray = Object.entries(topClients).map(([name, amount]) => ({ name, amount }));
        return { invoices: arr, statistics, monthlyRevenue, topClients: topClientsArray };
      }

      // Generic fallback for other report types could be added as needed
      return null;
    } catch (err) {
      console.warn('Error building fallback data for', type, err);
      return null;
    }
  };

  const renderCasesReport = () => {
    if (casesLoading && !sampleFallback) return <Typography component="span">Loading case reports...</Typography>;
    if (casesError && !sampleFallback) return <Typography component="span" color="error">Error loading case reports. Please try again.</Typography>;

    const fallback = buildFallbackFor('cases');
    const source = fallback || casesData?.data;
    if (!source) return <Typography component="span">No data available</Typography>;

    const { statistics, statusDistribution, typeDistribution, monthlyTrend } = source;

    const statusChartData = Object.entries(statusDistribution).map(([status, count]) => ({
      name: status,
      value: count,
    }));

    const typeChartData = Object.entries(typeDistribution).map(([type, count]) => ({
      name: type,
      value: count,
    }));

    const monthlyChartData = Object.entries(monthlyTrend).map(([month, count]) => ({
      name: month,
      cases: count,
    }));

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Cases
              </Typography>
              <Typography variant="h4" component="span">{statistics.totalCases}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Active Cases
              </Typography>
              <Typography variant="h4" component="span">{statistics.activeCases}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Closed Cases
              </Typography>
              <Typography variant="h4" component="span">{statistics.closedCases}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Pending Cases
              </Typography>
              <Typography variant="h4" component="span">{statistics.pendingCases}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Case Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Case Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={typeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Case Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="cases" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderFinancialReport = () => {
    if (financialLoading && !sampleFallback) return <Typography component="span">Loading financial reports...</Typography>;
    if (financialError && !sampleFallback) return <Typography component="span" color="error">Error loading financial reports. Please try again.</Typography>;

    const fallback = buildFallbackFor('financial');
    const source = fallback || financialData?.data;
    if (!source) return <Typography component="span">No data available</Typography>;

    const { statistics, statusDistribution, monthlyRevenue, topClients } = source;

    const revenueChartData = Object.entries(monthlyRevenue).map(([month, amount]) => ({
      month,
      revenue: amount,
    }));

    // Ensure topClients is an array
    const topClientsArray = Array.isArray(topClients) ? topClients : [];

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Invoiced
              </Typography>
              <Typography variant="h4" component="span">${statistics.totalInvoiced.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Paid
              </Typography>
              <Typography variant="h4" component="span">${statistics.totalPaid.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Outstanding
              </Typography>
              <Typography variant="h4" component="span">${statistics.totalOutstanding.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Paid Invoices
              </Typography>
              <Typography variant="h4" component="span">{statistics.paidInvoices}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Revenue Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Clients
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topClientsArray.map((client, index) => (
                      <TableRow key={index}>
                        <TableCell>{client.name}</TableCell>
                        <TableCell align="right">${client.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderClientsReport = () => {
    if (clientsLoading && !sampleFallback) return <Typography component="span">Loading client reports...</Typography>;
    if (clientsError && !sampleFallback) return <Typography component="span" color="error">Error loading client reports. Please try again.</Typography>;

    const source = clientsData?.data || (sampleFallback && sampleFallback.clients ? sampleFallback : null);
    if (!source) return <Typography component="span">No data available</Typography>;

    const { statistics, clientAcquisitionTrend, clientValue } = source.data || source;

    const acquisitionChartData = clientAcquisitionTrend ? Object.entries(clientAcquisitionTrend).map(([month, count]) => ({
      month,
      clients: count || 0,
    })) : [];

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Clients
              </Typography>
              <Typography variant="h4" component="span">{statistics?.totalClients || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Active Clients
              </Typography>
              <Typography variant="h4" component="span">{statistics?.activeClients || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                New Clients
              </Typography>
              <Typography variant="h4" component="span">{statistics?.newClients || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Client Acquisition Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={acquisitionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="clients" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Client Value Analysis
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Total Cases</TableCell>
                      <TableCell align="right">Active Cases</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(clientValue) ? clientValue.map((client, index) => (
                      <TableRow key={index}>
                        <TableCell>{client.name}</TableCell>
                        <TableCell align="right">{client.totalCases || 0}</TableCell>
                        <TableCell align="right">{client.activeCases || 0}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No client data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderProductivityReport = () => {
    if (productivityLoading && !sampleFallback) return <Typography component="span">Loading productivity reports...</Typography>;
    if (productivityError && !sampleFallback) return <Typography component="span" color="error">Error loading productivity reports. Please try again.</Typography>;

    const source = productivityData?.data || (sampleFallback && sampleFallback.tasks ? { tasks: sampleFallback.cases || [], documents: [], statistics: {} } : null);
    if (!source) return <Typography component="span">No data available</Typography>;

    const { statistics, userProductivity, monthlyProductivity } = source.data || source;

    const productivityChartData = monthlyProductivity ? Object.entries(monthlyProductivity).map(([month, data]) => ({
      month,
      total: data?.total || 0,
      completed: data?.completed || 0,
    })) : [];

    const userProductivityData = userProductivity ? Object.entries(userProductivity).map(([user, data]) => ({
      user,
      total: data?.total || 0,
      completed: data?.completed || 0,
      pending: data?.pending || 0,
      overdue: data?.overdue || 0,
    })) : [];

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Tasks
              </Typography>
              <Typography variant="h4" component="span">{statistics?.totalTasks || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Completed Tasks
              </Typography>
              <Typography variant="h4" component="span">{statistics?.completedTasks || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Completion Rate
              </Typography>
              <Typography variant="h4" component="span">{(statistics?.completionRate || 0).toFixed(1)}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Documents
              </Typography>
              <Typography variant="h4" component="span">{statistics?.totalDocuments || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Productivity Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={productivityChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="total" stroke="#8884d8" strokeWidth={2} />
                  <Line type="monotone" dataKey="completed" stroke="#00C49F" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Productivity
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userProductivityData.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>{user.user}</TableCell>
                        <TableCell align="right">{user.completed}</TableCell>
                        <TableCell align="right">{user.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderBillingReport = () => {
    if (billingLoading && !sampleFallback) return <Typography>Loading billing reports...</Typography>;
    if (billingError && !sampleFallback) return <Typography component="span" color="error">Error loading billing reports. Please try again.</Typography>;

    const fallback = buildFallbackFor('financial');
    const source = fallback || billingData?.data;
    if (!source) return <Typography>No data available</Typography>;

    const { statistics, statusBreakdown, typeBreakdown, monthlyBilling, topBillingClients } = source;

    const billingChartData = Object.entries(monthlyBilling).map(([month, amount]) => ({
      month,
      amount,
    }));

    return (
      <Grid container spacing={3}>
        {/* Statistics Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Invoices
              </Typography>
              <Typography variant="h4">{statistics.totalInvoices}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">${statistics.totalAmount.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Invoice
              </Typography>
              <Typography variant="h4">${statistics.averageInvoiceAmount.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Outstanding
              </Typography>
              <Typography variant="h4">${statistics.totalOutstanding.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Billing Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={billingChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="amount" fill="#FFBB28" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(typeBreakdown).map(([type, count]) => ({
                      name: type,
                      value: count,
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(typeBreakdown).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Billing Clients */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Billing Clients
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topBillingClients.map((client, index) => (
                      <TableRow key={index}>
                        <TableCell>{client.name}</TableCell>
                        <TableCell align="right">${client.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAnalyticsReport = () => {
    if (analyticsLoading) return <Typography component="span">Loading analytics...</Typography>;
    if (analyticsError) return <Typography component="span" color="error">Error loading analytics. Please try again.</Typography>;
    if (!analyticsData?.data) return <Typography component="span">No data available</Typography>;

    const { keyMetrics, revenueTrend, caseStatusDistribution, clientAcquisitionTrend } = analyticsData.data;

    // Ensure we have valid data before processing
    const analyticsChartData = revenueTrend ? Object.entries(revenueTrend).map(([month, revenue]) => ({
      month,
      revenue: revenue || 0,
    })) : [];

    const statusChartData = caseStatusDistribution ? Object.entries(caseStatusDistribution).map(([status, count]) => ({
      name: status,
      value: count || 0,
    })) : [];

    const acquisitionChartData = clientAcquisitionTrend ? Object.entries(clientAcquisitionTrend).map(([month, count]) => ({
      month,
      clients: count || 0,
    })) : [];

    return (
      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Revenue
              </Typography>
              <Typography variant="h4" component="span">${(keyMetrics?.totalRevenue || 0).toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Total Paid
              </Typography>
              <Typography variant="h4" component="span">${(keyMetrics?.totalPaid || 0).toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Completion Rate
              </Typography>
              <Typography variant="h4" component="span">{(keyMetrics?.completionRate || 0).toFixed(1)}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                Active Cases
              </Typography>
              <Typography variant="h4" component="span">{keyMetrics?.activeCases || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom component="span">
                New Clients
              </Typography>
              <Typography variant="h4" component="span">{keyMetrics?.newClients || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Trend Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Case Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Case Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Acquisition Trend */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Client Acquisition Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={acquisitionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="clients" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <ErrorBoundary>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Reports & Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={period}
                label="Time Period"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
                <MenuItem value="this_quarter">This Quarter</MenuItem>
                <MenuItem value="this_year">This Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {
                // TODO: Implement export functionality
                console.log('Export reports');
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="reports tabs">
            <Tab label="Cases" icon={<Folder />} iconPosition="start" />
            {showFinancialTabs && (
              <Tab label="Financial" icon={<AttachMoney />} iconPosition="start" />
            )}
            <Tab label="Clients" icon={<People />} iconPosition="start" />
            <Tab label="Productivity" icon={<Assignment />} iconPosition="start" />
            {showFinancialTabs && (
              <Tab label="Billing" icon={<AttachMoney />} iconPosition="start" />
            )}
            {showFinancialTabs && (
              <Tab label="Analytics" icon={<Assessment />} iconPosition="start" />
            )}
          </Tabs>
        </Box>

        {showFinancialTabs ? (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderCasesReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderFinancialReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderClientsReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {renderProductivityReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              {renderBillingReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
              {renderAnalyticsReport()}
            </TabPanel>
          </>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderCasesReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderClientsReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderProductivityReport()}
            </TabPanel>
          </>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default ReportsPage; 